/** Saved Email interaction settings, shared by Portal exports and Kit. */
export const EMAIL_INTERACTION_STYLES = [
  {
    value: 'lift',
    label: 'Soft lift',
    description: 'Gentle tint, soft shadow, and a small arrow movement.',
  },
  {
    value: 'ink_wash',
    label: 'Accent wash',
    description: 'A subtle brand tint with a crisp accent edge.',
  },
  {
    value: 'lift_highlight',
    label: 'Text highlight',
    description: 'A short marker swipe beneath the answer.',
  },
  {
    value: 'fill_sweep',
    label: 'Fill sweep',
    description: 'A soft fill travels across the answer.',
  },
  {
    value: 'none',
    label: 'None',
    description: 'Keep the original answer appearance.',
  },
] as const;
export type EmailInteractionStyle =
  (typeof EMAIL_INTERACTION_STYLES)[number]['value'];
export type EmailInteraction = {
  style: EmailInteractionStyle;
  /** Null follows the resolved Email theme. */
  accentColor: string | null;
  emphasis: 'subtle' | 'bold';
  motion: 'smooth' | 'off';
};
/** Preview only. Never persist this or apply it to copied output. */
export type EmailInteractionPreview = 'full' | 'simple' | 'static';
export const DEFAULT_EMAIL_INTERACTION: Readonly<EmailInteraction> = Object
  .freeze({
    style: 'lift',
    accentColor: null,
    emphasis: 'subtle',
    motion: 'smooth',
  });
export function parseEmailInteraction(value: unknown): EmailInteraction | null {
  if (value === undefined) return { ...DEFAULT_EMAIL_INTERACTION };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const item = value as Record<string, unknown>;
  if (
    Object.keys(item).length !== 4 ||
    !EMAIL_INTERACTION_STYLES.some((style) => style.value === item.style) ||
    !(
      item.accentColor === null ||
      (typeof item.accentColor === 'string' &&
        /^#[0-9a-f]{6}$/i.test(item.accentColor))
    ) ||
    (item.emphasis !== 'subtle' && item.emphasis !== 'bold') ||
    (item.motion !== 'smooth' && item.motion !== 'off')
  ) {
    return null;
  }
  return {
    style: item.style as EmailInteractionStyle,
    accentColor: typeof item.accentColor === 'string'
      ? item.accentColor.toLowerCase()
      : null,
    emphasis: item.emphasis,
    motion: item.motion,
  };
}
